/* eslint-disable @typescript-eslint/no-explicit-any */
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Observable, Subject, takeUntil } from 'rxjs';

import { Environment, ENVIRONMENT } from '@hmax-order/frontend/environment';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { AcceptedCurrency, Column } from '../../interfaces';
import {
  CheckUnderlinePipe,
  ColumnAlignmentPipe,
  ColumnFormattedValuePipe,
  ColumnNamesPipe,
  ColumnValuePipe,
  FormatColumnTextPipe,
  HideBorderBottomPipe,
  TooltipTextPipe,
} from '../pipes';
import { TableService } from '../table/table.service';
import { ButtonIconSmallComponent } from '../buttons/button-icon-small/button-icon-small.component';

interface ExampleFlatNode {
  expandable: boolean;
  name: string;
  count: number;
  level: number;
}

@Component({
  selector: 'hmax-table-tree',
  standalone: true,
  imports: [
    ButtonIconSmallComponent,
    CdkDrag,
    CdkDropList,
    CheckUnderlinePipe,
    ColumnAlignmentPipe,
    ColumnFormattedValuePipe,
    ColumnNamesPipe,
    ColumnValuePipe,
    CommonModule,
    FormatColumnTextPipe,
    FormsModule,
    HideBorderBottomPipe,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MatTreeModule,
    RouterModule,
    TooltipTextPipe,
    TranslocoModule,
    CapitalizePipe,
  ],
  templateUrl: './table-tree.component.html',
  /**
   * O que os pipes de célula injetam, como a `hmax-table` provê: o `columnFormattedValue` (data, moeda,
   * `NUMBER`) e o `formatColumnText`, que usa o `CapitalizePipe`. Sem isto a coluna formatada derruba a
   * tela com NG0201 — só não derrubava antes porque a única árvore do produto, a de grupos, não tinha
   * coluna formatada.
   */
  providers: [CapitalizePipe, ColumnValuePipe, CurrencyPipe, DatePipe, DecimalPipe],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./table-tree.component.scss'],
})
export class TableTreeComponent implements OnInit, OnDestroy {
  private readonly _env: Environment = inject(ENVIRONMENT);
  private _tableService = inject(TableService);
  private treeFlattener!: MatTreeFlattener<any, any>;
  private _unsubscribeAll = new Subject<any>();

  @Input() columns: Column[] = [];
  @Input() apiUrl = this._env.apiUrl;
  @Input() currency: AcceptedCurrency = 'BRL';
  @Input() hideBottomBorder = false;
  @Input() onClickRow?: (row: any) => void;
  @Input() selectOptions: { [key: string]: any[] } = {};
  @Input() expandAllOnInit = false;
  @Input() isZebraEnabled = false;
  @Input() showMasterExpandButton = false;
  /**
   * F15 — arrastar e soltar LINHAS, entre irmãos. Ligado, a árvore emite `rowDropped` com a linha arrastada e
   * a linha cujo lugar ela tomou, e quem usa reordena o próprio dado: os filhos acompanham o pai porque a árvore
   * é remontada a partir dele. A linha só cai onde `canDropRow` deixa; sem ele, no mesmo nível.
   */
  @Input() reorderRows = false;
  /** F15 — onde a linha arrastada pode cair: recebe a arrastada e a que ocupava o lugar. */
  @Input() canDropRow?: (dragged: any, target: any) => boolean;

  // Recebimento de dados para montar a tabela
  @Input() data: any; // Array de objetos
  @Input() dataObservable!: Observable<any>;
  @Input() resource = ''; // Url da API para consulta

  @Output() actionClick = new EventEmitter<any>();
  @Output() cellClick = new EventEmitter<any>();
  @Output() selectChange = new EventEmitter<{ row: any; column: Column; value: any }>(); // Added for select change
  /** F15 — linha solta no arrastar (`reorderRows`): a arrastada e a linha cujo lugar ela tomou. */
  @Output() rowDropped = new EventEmitter<{ row: any; target: any }>();

  /** F15 — as linhas na ordem em que estavam na tela quando o arrasto começou: o destino é lido contra ela. */
  private _rowsAtDragStart: any[] = [];

  /** Mesma lista da `hmax-table`, e pelo mesmo motivo do `NUMBER`: o ramo de texto esconde o `0`. */
  readonly typesColumnFormatted = [
    'DATE',
    'DATE4',
    'DATETIME',
    'DAYMONTHTIME',
    'TIME',
    'TIMESTAMP',
    'CURRENCY',
    'FLOAT2',
    'FLOAT4',
    'NUMBER',
  ];
  isTreeExpanded = false;
  /**
   * Algum nó tem filho? O botão de expandir da primeira coluna só existe quando sim. Numa lista plana ele
   * ficava invisível em toda linha, mas guardando ~48px à esquerda: o conteúdo da primeira coluna saía
   * deslocado do título, que centraliza sem ele (F15, posição com as setas na tela de grupos).
   */
  hasExpandableRows = false;
  dataSource!: MatTreeFlatDataSource<any, any, any>;
  treeControl = new FlatTreeControl<ExampleFlatNode>(
    (node) => node.level,
    (node) => node.expandable,
  );

  get url(): string {
    return `${this.apiUrl}/${this.resource}`;
  }

  ngOnInit(): void {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      (node) => node.level,
      (node) => node.expandable,
      (node) => node.children,
    );
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.loadData();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private transformer = (node: any, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node?.name,
      // count: node.count,
      level: level,
      ...node,
    };
  };

  /**
   * Carregar dados a partir de um observable
   */
  private loadDataObservable(): void {
    this.dataObservable.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (data: any[]) => {
        return this.setData(data);
      },
    });
  }

  /**
   * Carregar dados a partir de uma URL
   */
  private loadDataResource(): void {
    this._tableService
      .getDataApi(this.url)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response: any) => {
          return this.setData(response);
        },
      });
  }

  /**
   * Setar dados para tabela
   */
  private setData(data: any) {
    this.data = data;
    this.dataSource.data = this.data;
    this.hasExpandableRows = this.treeControl.dataNodes?.some((node) => node.expandable) ?? false;
    this.checkExpandAll();
  }

  /**
   * Verificar se deve exibir a arvore com todos os nós abertos
   */
  private checkExpandAll() {
    if (this.expandAllOnInit) {
      this.expandAll();
    }
  }

  /**
   * Verificar dados informados para carregamento da tabela
   */
  loadData(): void {
    if (this.dataObservable) {
      return this.loadDataObservable();
    } else if (this.resource) {
      return this.loadDataResource();
    } else if (this.data) {
      return this.setData(this.data);
    }
  }

  hasChild = (_: number, node: any) => node?.expandable;

  /**
   * F15 — o CDK pergunta, a cada posição que a linha arrastada visita, se ela pode ficar ali. A resposta vem da
   * linha que ocupava aquela posição quando o arrasto começou: só entre irmãos, ou onde `canDropRow` deixar.
   * Arrow function para o CDK chamá-la com o `this` da árvore.
   */
  readonly sortPredicate = (index: number, drag: CdkDrag): boolean => {
    const target = this._rowsAtDragStart[index];
    return !!target && this.canDrop(drag.data, target);
  };

  /** F15 — fotografa a ordem das linhas na tela no começo do arrasto. */
  onDragStarted(): void {
    this._rowsAtDragStart = this.visibleRows();
  }

  /** F15 — linha solta: avisa qual linha ela tomou o lugar. Solta no mesmo lugar ou fora dos irmãos, nada. */
  onRowDropped(event: CdkDragDrop<unknown>): void {
    const row = event.item.data;
    const target = this._rowsAtDragStart[event.currentIndex];
    this._rowsAtDragStart = [];
    if (event.previousIndex === event.currentIndex || !target || target === row || !this.canDrop(row, target)) {
      return;
    }
    this.rowDropped.emit({ row, target });
  }

  private canDrop(dragged: any, target: any): boolean {
    return this.canDropRow ? this.canDropRow(dragged, target) : dragged?.level === target?.level;
  }

  /**
   * Linhas na ordem em que a árvore as desenha: o nó aparece quando todos os ancestrais estão abertos. É a
   * mesma regra do `MatTreeFlatDataSource`, e é essa a ordem dos índices que o arrasto informa.
   */
  private visibleRows(): any[] {
    const rows: any[] = [];
    const openAtLevel: boolean[] = [];
    for (const node of this.treeControl.dataNodes ?? []) {
      const shown = node.level === 0 || !!openAtLevel[node.level - 1];
      if (shown) {
        rows.push(node);
      }
      openAtLevel[node.level] = shown && this.treeControl.isExpanded(node);
    }
    return rows;
  }

  /**
   * Método acionado quando usuário clica em qualquer parte de uma linha na tabela
   * @param index índice da linha na tabela
   * @param row dados da linha na tabela (visíveis ou não)
   */
  doRowClick(index: number, row: any): void {
    // Incluir índice como uma propriedade da linha
    row = { index, ...row };
    if (this.onClickRow) {
      this.onClickRow(row);
    }
  }

  /**
   * Acionado quando o usuário clica na célula com a coluna contendo o atributo cellClick
   * @param index índice da linha na tabela
   * @param row dados da linha na tabela (visíveis ou não)
   */
  doCellClick(column: Column, index: number, row: any): void {
    row = { index, columnName: column?.name, ...row };
    if (column?.cellClick) {
      this.cellClick.emit(row);
    }
  }

  /**
   * Método acionado quando um menu da linha é clicado
   * @param method nome do método a ser acionado
   * @param index índice da linha na tabela
   * @param row dados da linha na tabela (visíveis ou não)
   */
  doActionClick(method: string, index: number, row: any): void {
    // Incluir nome do método e índice como uma propriedade da linha
    row = { method, index, ...row };
    this.actionClick.emit(row);
  }

  /**
   * Método acionado quando o valor de um select é alterado
   * @param event evento de alteração
   * @param column coluna
   * @param row linha
   */
  onSelectChange(event: any, column: Column, row: any): void {
    this.selectChange.emit({ row, column, value: event.value });
  }

  /** Função para expandir todos os nós */
  expandAll(): void {
    this.treeControl.expandAll();
    this.isTreeExpanded = true;
  }

  /** Função para colapsar todos os nós */
  collapseAll(): void {
    this.treeControl.collapseAll();
    this.isTreeExpanded = false;
  }

  /** Função para alternar entre expandir e colapsar todos os nós */
  toggleExpandCollapse(): void {
    const allExpanded =
      this.treeControl?.dataNodes?.length && this.treeControl.dataNodes.every((node) => this.treeControl.isExpanded(node));

    if (allExpanded) {
      this.collapseAll();
      this.isTreeExpanded = false;
    } else {
      this.expandAll();
      this.isTreeExpanded = true;
    }
  }

  get maxTreeLevel(): number {
    const nodes = this.treeControl?.dataNodes ?? [];
    return nodes.length ? Math.max(...nodes.map((node) => node.level)) + 1 : 0;
  }

  expandToLevel(level: number): void {
    this.treeControl.collapseAll();
    const nodes = this.treeControl?.dataNodes ?? [];
    nodes.filter((node) => node.expandable && node.level < level - 1).forEach((node) => this.treeControl.expand(node));
    this.isTreeExpanded = nodes.some((node) => this.treeControl.isExpanded(node));
  }
}
